import { DeleteForeverOutlined, EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, NumbersOutlined, PhoneOutlined, SettingsOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageAppBar } from "../../components/pageAppBar";
import { UserContext } from "../../contexts/UserContext";
import { createCallerIdConfiguration, updateCallerIdConfiguration } from "../../graphql/mutations";
import { callerIdsByTenant, getCallerIdConfiguration } from "../../graphql/queries";
import { timezones } from '../../models/timezones';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from "../../theme";
import { ConfirmDialog } from "src/components/confirmDialog/confirmDialog";


export function CallerIdConfig() {
    const { id } = useParams();
    const navigate = useNavigate();
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);
    const [navigateConfirmOpen, setNavigateConfirmOpen] = useState(false);
    const [newConfigOpen, setNewConfigOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newConfigType, setNewConfigType] = useState('');
    const [newConfigQuery, setNewConfigQuery] = useState({
        field: '',
        operator: '',
        value: ''
    });
    const [newConfigPool, setNewConfigPool] = useState([]);
    const [availableNumbers, setAvailableNumbers] = useState([]);
    const [openRow, setOpenRow] = useState(null);

    const queryOperations = [
        { op: 'ne', display: 'Not equal' },
        { op: 'eq', display: 'Equal' },
        { op: 'le', display: 'Less than or equal to' },
        { op: 'lt', display: 'Less than' },
        { op: 'ge', display: 'Greater than or equal to' },
        { op: 'gt', display: 'Greater than' },
        { op: 'beginsWith', display: 'Begins with' },
        { op: 'in', display: 'In' },
    ];

    const types = [
        { value: 'random', label: 'Random' },
        { value: 'contact', label: 'Contact Query' },
        { value: 'schedule', label: 'Scheduled' },
    ];

    const daysOfWeek = [
        { value: 'Sun', label: 'Sunday' },
        { value: 'Mon', label: 'Monday' },
        { value: 'Tue', label: 'Tuesday' },
        { value: 'Wed', label: 'Wednesday' },
        { value: 'Thu', label: 'Thursday' },
        { value: 'Fri', label: 'Friday' },
        { value: 'Sat', label: 'Saturday' },
    ];

    const newProfile = {
        name: '',
        tenant: userContext.tenantId,
        configs: []
    };

    useEffect(() => {
        if (userContext.tenantId) {
            getConfig();
            getAvailableNumbers();
        }
    }, [id, userContext.tenantId]);

    async function getConfig() {
        setLoading(true);

        if (id !== 'new') {
            const result = await client.graphql({
                query: getCallerIdConfiguration,
                variables: { id },
            });
            if (result && result.data && result.data.getCallerIdConfiguration) {
                setProfile(result.data.getCallerIdConfiguration);
            } else {
                enqueueSnackbar('Error fetching profile data', { variant: 'error' });
            }

            setLoading(false);
        } else {
            setProfile(newProfile);
        }
    }

    async function getAvailableNumbers() {
        setLoading(true);
        const savedCallerIds = await client.graphql({
            query: callerIdsByTenant,
            variables: { tenant: userContext.tenantId },
        });
        setAvailableNumbers(
            savedCallerIds.data.callerIdsByTenant.items.sort(
                (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
            ),
        );
        setLoading(false);
    }

    function parseQuery(query) {
        try {
            return typeof query === 'string'
                ? JSON.parse(query)
                : query;
        } catch {
            return null;
        }
    }

    function stringifyTimewindowTime(time) {
        const hour = time.split(':')[0]
        const minute = time.split(':')[1]
        return `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${minute.toString().padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
    }

    function getQueryDisplay(config) {
        const query = parseQuery(config.query);

        if (!query) return 'None';

        if (config.type === 'contact') {
            const operation =
                queryOperations.find(op => op.op === query.operator)?.display ||
                query.operator;

            return `${query.field} ${operation} ${query.value}`;
        }

        if (config.type === 'schedule') {
            const days = query.days && query.days.length > 0
                ? query.days.join(', ')
                : 'Every day';
            return `${days} · ${stringifyTimewindowTime(query.startTime)} - ${stringifyTimewindowTime(query.endTime)} ${timezones.find((tz) => query.timezone === tz.offset)?.label}`;
        }

        return 'Random Selection';
    }

    const saveProfile = async (values) => {
        setLoading(true);

        try {

            const input = {
                ...values,
                tenant: userContext.tenantId
            };

            if (id === 'new') {
                await client.graphql({
                    query: createCallerIdConfiguration,
                    variables: { input },
                });
            } else {
                delete input.createdAt;
                delete input.updatedAt;
                await client.graphql({
                    query: updateCallerIdConfiguration,
                    variables: { input },
                });
            }

            navigate('/caller-id/configurations');

        } catch (error) {
            console.error('Error saving profile:', error);

            enqueueSnackbar(
                'Error saving profile',
                { variant: 'error' }
            );
        }

        setLoading(false);
    };

    function getRow({
        openRow,
        setOpenRow,
        config,
        index,
        setFieldValue,
        values
    }) {

        const isOpen = openRow === index;

        return (
            <>
                <TableRow
                    hover
                    key={index}
                    sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        '& td, & th': {
                            borderBottom: isOpen ? 'none' : undefined
                        }
                    }}
                >
                    <TableCell onClick={() => setOpenRow(isOpen ? null : index)}>
                        {isOpen
                            ? <KeyboardArrowUpOutlined />
                            : <KeyboardArrowDownOutlined />
                        }
                    </TableCell>

                    <TableCell onClick={() => setOpenRow(isOpen ? null : index)}>
                        <Typography fontWeight={600}>
                            {types.find(t => t.value === config.type)?.label || config.type}
                        </Typography>
                    </TableCell>

                    <TableCell onClick={() => setOpenRow(isOpen ? null : index)}>
                        <Typography variant="body2">
                            {getQueryDisplay(config)}
                        </Typography>
                    </TableCell>

                    <TableCell onClick={() => setOpenRow(isOpen ? null : index)}>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {config.pool.slice(0, 3).map((number, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '6px',
                                        backgroundColor: 'action.hover',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {number}
                                </Box>
                            ))}

                            {config.pool.length > 3 && (
                                <Typography variant="caption">
                                    +{config.pool.length - 3} more
                                </Typography>
                            )}
                        </Box>
                    </TableCell>

                    <TableCell>
                        <Box display="flex">
                            <IconButton
                                onClick={() => {
                                    const parsed = parseQuery(config.query);

                                    setEditingIndex(index);
                                    setNewConfigType(config.type);

                                    if (config.type === 'contact') {
                                        setNewConfigQuery(parsed || {});
                                    }

                                    if (config.type === 'schedule') {
                                        setNewConfigQuery(parsed || {});
                                    }

                                    setNewConfigPool(config.pool);
                                    setNewConfigOpen(true);
                                }}
                            >
                                <EditOutlined color="primary" />
                            </IconButton>

                            <IconButton
                                onClick={() => {
                                    const newConfigs = [...values.configs];
                                    newConfigs.splice(index, 1);

                                    setFieldValue('configs', newConfigs);
                                }}
                            >
                                <DeleteForeverOutlined color="error" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>

                <TableRow
                    sx={{
                        display: isOpen ? 'table-row' : 'none',
                        '& td': {
                            borderBottom: 'none',
                            paddingBottom: 0,
                            paddingTop: 0,
                        }
                    }}
                >
                    <TableCell colSpan={6}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: 'action.hover',
                                    borderTop: '1px solid',
                                    borderTopColor: 'divider'
                                }}
                            >

                                <Typography variant="subtitle2" gutterBottom>
                                    Assigned Numbers
                                </Typography>

                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {config.pool.map((number, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: '8px',
                                                backgroundColor: 'background.paper',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {number}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    }

    return (
        <Formik
            initialValues={profile || newProfile}
            enableReinitialize
            onSubmit={async (values) => {
                await saveProfile(values);
            }}
        >
            {({ values, handleChange, handleSubmit, dirty, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <Box>
                        <PageAppBar
                            title={id === 'new' ? 'New Caller ID Configuration' : `${profile ? profile.name : 'Update Caller ID Configuration'}`}
                            description="Configure how Caller ID should be determined for outbound calls."
                            actionOneText="Save"
                            actionOneHandler={handleSubmit}
                            actionOneDisabled={!dirty}
                            actionTwoText="Close"
                            actionTwoHandler={() => {
                                if (dirty) {
                                    setNavigateConfirmOpen(true);
                                } else {
                                    navigate('/caller-id/configurations');
                                }
                            }}
                        />

                        <Card variant='outlined' sx={cardStyle}>

                            <CardContent>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Configuration Name"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        margin="normal"
                                    />
                                    <Card style={cardStyle} elevation={0}>
                                        <CardHeader action={
                                            <Button
                                                sx={actionOneButtonStyle}
                                                onClick={() => {
                                                    setEditingIndex(null);
                                                    setNewConfigOpen(true);
                                                }}
                                            >+ Add Rule</Button>
                                        } title="Caller ID Rules" />
                                        <CardContent>
                                            <Paper variant="outlined" sx={{ borderRadius: '8px' }} elevation={0}>
                                                <Table>
                                                    <TableHead sx={{ backgroundColor: 'action.hover' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ width: '40px' }} />
                                                            <TableCell>Type</TableCell>
                                                            <TableCell>Rule</TableCell>
                                                            <TableCell>Numbers</TableCell>
                                                            <TableCell width={120}>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {values.configs.map((config, index) => (
                                                            getRow({
                                                                openRow,
                                                                setOpenRow,
                                                                config,
                                                                index,
                                                                setFieldValue,
                                                                values
                                                            })
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Paper>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Dialog open={newConfigOpen}>
                        <DialogTitle>{editingIndex !== null ? 'Edit Caller ID Configuration' : 'New Caller ID Configuration'}</DialogTitle>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <FormControl size="small" fullWidth margin="normal">
                                    <InputLabel id="config-type-label">Configuration Type</InputLabel>
                                    <Select
                                        labelId="config-type-label"
                                        name="newConfigType"
                                        value={newConfigType}
                                        defaultValue="random"
                                        onChange={(e) => setNewConfigType(e.target.value)}
                                        label="Configuration Type"
                                    >
                                        {types.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {(newConfigType && newConfigType !== 'random') && (
                                    <Card variant='outlined' sx={cardStyle}>
                                        <CardHeader title="Configuration Rules"
                                            avatar={
                                                <SettingsOutlined color="primary" />
                                            }
                                            titleTypographyProps={{ variant: 'h6', fontSize: '16px' }} />
                                        <Divider />
                                        <CardContent>

                                            {newConfigType === 'contact' && (
                                                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={2}>
                                                    <FormControl size="small" fullWidth>
                                                        <InputLabel id="contact-query-type-label">Field</InputLabel>
                                                        <Select
                                                            labelId="contact-query-type-label"
                                                            name="newConfigQuery.field"
                                                            value={newConfigQuery.field}
                                                            onChange={(e) => setNewConfigQuery({ ...newConfigQuery, field: e.target.value })}
                                                            label="Contact Query Field"
                                                        >
                                                            {userContext.queryFields.list?.map(
                                                                (field, index) => (
                                                                    <MenuItem key={index} value={field.name}>
                                                                        {field.label}
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </Select>
                                                    </FormControl>

                                                    <FormControl size="small" fullWidth>
                                                        <InputLabel id="contact-query-pool-label">Operator</InputLabel>
                                                        <Select
                                                            labelId="contact-query-pool-label"
                                                            name="newConfigQuery.operator"
                                                            value={newConfigQuery.operator}
                                                            onChange={(e) => setNewConfigQuery({ ...newConfigQuery, operator: e.target.value })}
                                                            label="Pool Operator"
                                                        >
                                                            {queryOperations?.map((op) => (
                                                                <MenuItem key={op.op} value={op.op}>
                                                                    {op.display}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>

                                                    <TextField
                                                        size="small"
                                                        name="newConfigQuery.value"
                                                        value={newConfigQuery.value}
                                                        onChange={(e) => setNewConfigQuery({ ...newConfigQuery, value: e.target.value })}
                                                        label="Value"
                                                    />
                                                </Box>
                                            )}

                                            {newConfigType === 'schedule' && (
                                                <Box display="flex" flexDirection="column" gap={2} mt={2}>
                                                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel id="timezone-label">Timezone</InputLabel>
                                                            <Select
                                                                labelId="timezone-label"
                                                                name="newConfigQuery.timezone"
                                                                value={newConfigQuery.timezone}
                                                                onChange={(e) => setNewConfigQuery({ ...newConfigQuery, timezone: e.target.value })}
                                                                label="Timezone"
                                                            >
                                                                {timezones.map((tz) => (
                                                                    <MenuItem key={tz.value} value={tz.offset}>{tz.label}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <TextField
                                                            size="small"
                                                            name="newConfigQuery.startTime"
                                                            value={newConfigQuery.startTime || '00:00'}
                                                            onChange={(e) => setNewConfigQuery({ ...newConfigQuery, startTime: e.target.value })}
                                                            label="Start Time"
                                                            type="time"
                                                        />
                                                        <TextField
                                                            size="small"
                                                            name="newConfigQuery.endTime"
                                                            value={newConfigQuery.endTime || '00:00'}
                                                            onChange={(e) => setNewConfigQuery({ ...newConfigQuery, endTime: e.target.value })}
                                                            label="End Time"
                                                            type="time"
                                                        />
                                                    </Box>
                                                    <FormControl size="small" fullWidth>
                                                        <InputLabel id="days-of-week-label">Days of Week</InputLabel>
                                                        <Select
                                                            labelId="days-of-week-label"
                                                            multiple
                                                            value={newConfigQuery.days || []}
                                                            onChange={(e) => setNewConfigQuery({ ...newConfigQuery, days: e.target.value })}
                                                            label="Days of Week"
                                                            renderValue={(selected) => selected.map(d => daysOfWeek.find(day => day.value === d)?.label || d).join(', ')}
                                                        >
                                                            {daysOfWeek.map((day) => (
                                                                <MenuItem key={day.value} value={day.value}>
                                                                    <Checkbox checked={(newConfigQuery.days || []).includes(day.value)} />
                                                                    <ListItemText primary={day.label} />
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            )}

                                        </CardContent>
                                    </Card>)}


                                <Box display="grid" gridTemplateColumns="1fr">
                                    <Card variant='outlined' sx={cardStyle}>
                                        <CardHeader title="Available Numbers"
                                            avatar={
                                                <NumbersOutlined color="primary" />
                                            }
                                            titleTypographyProps={{ variant: 'h6', fontSize: '16px' }} />
                                        <Divider />
                                        <CardContent>
                                            <List>
                                                {availableNumbers.map((number, index) => (
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" key={index} mb={1}>
                                                        <Box display="flex" alignItems="center" gap={2}>
                                                            <PhoneOutlined color="primary" />
                                                            <Box>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    fontWeight={700}
                                                                    noWrap
                                                                >
                                                                    {number.name ||
                                                                        number.number}
                                                                </Typography>

                                                                {number.name && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {number.number}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Switch
                                                            checked={newConfigPool.includes(number.number)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setNewConfigPool([...newConfigPool, number.number]);
                                                                } else {
                                                                    setNewConfigPool(newConfigPool.filter(num => num !== number.number));
                                                                }

                                                            }}
                                                        />
                                                    </Box>
                                                ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {
                                setNewConfigOpen(false);
                                setEditingIndex(null);
                                setNewConfigType('');
                                setNewConfigQuery({
                                    field: '',
                                    operator: '',
                                    value: '',
                                    timezone: '',
                                    startTime: '',
                                    endTime: '',
                                    days: []
                                });
                                setNewConfigPool([]);
                            }} sx={actionTwoButtonStyle}>Cancel</Button>
                            <Button
                                onClick={() => {

                                    const query =
                                        newConfigType === 'contact'
                                            ? JSON.stringify({
                                                field: newConfigQuery.field,
                                                operator: newConfigQuery.operator,
                                                value: newConfigQuery.value
                                            })
                                            : newConfigType === 'schedule'
                                                ? JSON.stringify({
                                                    timezone: newConfigQuery.timezone,
                                                    startTime: newConfigQuery.startTime,
                                                    endTime: newConfigQuery.endTime,
                                                    days: newConfigQuery.days || []
                                                })
                                                : null;

                                    const configEntry = {
                                        type: newConfigType,
                                        query,
                                        pool: newConfigPool
                                    };

                                    let newConfigs;
                                    if (editingIndex !== null) {
                                        newConfigs = [...values.configs];
                                        newConfigs[editingIndex] = configEntry;
                                    } else {
                                        newConfigs = [...values.configs, configEntry];
                                    }

                                    setFieldValue('configs', newConfigs);

                                    setNewConfigOpen(false);
                                    setEditingIndex(null);

                                    setNewConfigType('');
                                    setNewConfigQuery({
                                        field: '',
                                        operator: '',
                                        value: '',
                                        timezone: '',
                                        startTime: '',
                                        endTime: '',
                                        days: []
                                    });
                                    setNewConfigPool([]);
                                }}
                                sx={actionOneButtonStyle}
                            >
                                {editingIndex !== null ? 'Save' : 'Add'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <ConfirmDialog
                        open={navigateConfirmOpen}
                        actionOneHandler={() => {
                            setNavigateConfirmOpen(false);
                            navigate(-1);
                        }}
                        actionTwoHandler={() => setNavigateConfirmOpen(false)}
                        title="Discard Changes"
                        description="Are you sure you want to discard your changes?"
                        actionOneText="Discard"
                        actionTwoText="Cancel"
                    />
                </form>
            )}
        </Formik>
    );
}
